import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
  Avatar,
  Badge
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccountCircle as UserIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
  LocalShipping as ShippingIcon,
  VerifiedUser as VerifiedIcon
} from '@mui/icons-material';
import './DetailCard.css';

export interface DetailField {
  name: string;
  label: string;
  value: any;
  type?: 'text' | 'chip' | 'status' | 'address' | 'phone' | 'email' | 'date' | 'currency' | 'percentage';
  chipType?: 'local' | 'outside' | 'admin' | 'user' | 'active' | 'inactive' | 'success' | 'warning' | 'error';
  fullWidth?: boolean;
  icon?: React.ReactNode;
}

export interface DetailSection {
  title: string;
  icon?: React.ReactNode;
  fields: DetailField[];
  color?: string;
}

export interface DetailCardProps {
  id: string;
  subtitle?: string;
  sections: DetailSection[];
  onEdit?: () => void;
  onDelete?: () => void;
  onView?: () => void;
  showActions?: boolean;
  avatar?: string;
  status?: 'active' | 'inactive' | 'pending';
}

const DetailCard: React.FC<DetailCardProps> = ({
  id,
  subtitle,
  sections,
  onEdit,
  onDelete,
  onView,
  showActions = true,
  avatar,
  status
}) => {
  const renderFieldValue = (field: DetailField) => {
    const { value, type = 'text', chipType } = field;
    
    if (!value || value === '-' || value === '') {
      return (
        <Box className="detail-field-value empty">
          <Typography variant="body2" className="empty-text">
            Not provided
          </Typography>
        </Box>
      );
    }

    switch (type) {
      case 'chip':
        return (
          <Chip
            label={value}
            className={`detail-chip ${chipType || ''}`}
            size="medium"
            variant="filled"
          />
        );
      
      case 'status':
        return (
          <Box className={`detail-status ${chipType || 'active'}`}>
            <Box className="status-dot" />
            <Typography variant="body2" className="status-text">
              {value}
            </Typography>
          </Box>
        );
      
      case 'address':
        return (
          <Box className="detail-field-value address">
            <LocationIcon className="field-icon" />
            <Typography variant="body1" className="address-text">
              {value}
            </Typography>
          </Box>
        );
      
      case 'phone':
        return (
          <Box className="detail-field-value phone">
            <PhoneIcon className="field-icon" />
            <Typography variant="body1" className="phone-text">
              {value}
            </Typography>
          </Box>
        );
      
      case 'email':
        return (
          <Box className="detail-field-value email">
            <EmailIcon className="field-icon" />
            <Typography variant="body1" className="email-text">
              {value}
            </Typography>
          </Box>
        );
      
      case 'date':
        return (
          <Box className="detail-field-value date">
            <ScheduleIcon className="field-icon" />
            <Typography variant="body1" className="date-text">
              {new Date(value).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
          </Box>
        );

      case 'currency':
        return (
          <Box className="detail-field-value currency">
            <Typography variant="body1" className="currency-text">
              ₹{Number(value).toLocaleString('en-IN')}
            </Typography>
          </Box>
        );

      case 'percentage':
        return (
          <Box className="detail-field-value percentage">
            <Typography variant="body1" className="percentage-text">
              {value}%
            </Typography>
          </Box>
        );
      
      default:
        return (
          <Box className="detail-field-value text">
            <Typography variant="body1" className="text-value">
              {value}
            </Typography>
          </Box>
        );
    }
  };

  const getSectionIcon = (title: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      'Basic Information': <BusinessIcon />,
      'Contact Information': <PhoneIcon />,
      'Address Information': <LocationIcon />,
      'Tax Information': <ReceiptIcon />,
      'System Information': <UserIcon />,
      'Product Details': <BusinessIcon />,
      'User Information': <UserIcon />,
      'Shipping Information': <ShippingIcon />,
    };

    return iconMap[title] || <BusinessIcon />;
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'inactive': return '#ef4444';
      case 'pending': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  return (
    <Box className="detail-card">
      {/* Card Header */}
      <Box className="detail-card-header">
        <Box className="detail-card-main">
          <Box className="detail-card-avatar-section">
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <Box 
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(status) }}
                />
              }
            >
              <Avatar className="detail-avatar">
                {avatar ? (
                  <img src={avatar} alt={id} />
                ) : (
                  <BusinessIcon />
                )}
              </Avatar>
            </Badge>
          </Box>
          
          <Box className="detail-card-info">
            <Typography className="detail-card-id">
              {id}
            </Typography>
            {subtitle && (
              <Typography className="detail-card-subtitle">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        
        {showActions && (
          <Box className="detail-card-actions">
            {onView && (
              <IconButton
                className="detail-card-action-btn view-btn"
                onClick={onView}
                title="View Details"
              >
                <ViewIcon />
              </IconButton>
            )}
            {onEdit && (
              <IconButton
                className="detail-card-action-btn edit-btn"
                onClick={onEdit}
                title="Edit"
              >
                <EditIcon />
              </IconButton>
            )}
            {onDelete && (
              <IconButton
                className="detail-card-action-btn delete-btn"
                onClick={onDelete}
                title="Delete"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      <Divider className="detail-card-divider" />

      {/* Card Content */}
      <Box className="detail-card-content">
        <Box className="detail-sections">
          {sections.map((section, sectionIndex) => (
            <Box key={sectionIndex} className="detail-section">
              <Box className="detail-section-header">
                <Box className="section-icon-container">
                  {section.icon || getSectionIcon(section.title)}
                </Box>
                <Typography className="detail-section-title">
                  {section.title}
                </Typography>
              </Box>
              
              <Box className="detail-fields-grid">
                {section.fields.map((field, fieldIndex) => (
                  <Box
                    key={fieldIndex}
                    className={`detail-field ${field.fullWidth ? 'full-width' : ''}`}
                  >
                    <Typography className="detail-field-label">
                      {field.label}
                    </Typography>
                    {renderFieldValue(field)}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default DetailCard; 